/**
 * All documentation comments for CSS properties should be declared within this
 * file to ensure that TypeScript includes them in the type declaration for CSS.
 */ import { tokens } from '@sail/tokens/deprecated';
import { backgroundColor, borderBottomColor, borderColor, borderLeftColor, borderRightColor, borderTopColor, color, fill, stroke, textColor } from './propertiesColor';
import { font, fontFamily, fontSize, fontWeight, lineHeight } from './propertiesFont';
import { format, property } from './util';
const sizeProperty = property([
    tokens.size,
    tokens.space
]).accept(format.px);
const propertiesSize = {
    /**
   * Sets the `height` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/height
   */ height: sizeProperty,
    /**
   * Sets the `min-width` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/min-width
   */ minWidth: sizeProperty,
    /**
   * Sets the `max-width` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/max-width
   */ maxWidth: sizeProperty,
    /**
   * Sets the `min-height` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/min-height
   */ minHeight: sizeProperty,
    /**
   * Sets the `max-height` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/max-height
   */ maxHeight: sizeProperty,
    /**
   * Sets the `width` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/width
   */ width: sizeProperty
};
const spaceProperty = property([
    tokens.space
]).accept(format.px);
const propertiesSpace = {
    /**
   * Bleeding an element allows it to extend past the boundaries of its container.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/bleed
   */ bleed: spaceProperty,
    /**
   * Allows an element to extend past the boundaries of its container on the x axis.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/bleed
   */ bleedX: spaceProperty,
    /**
   * Allows an element to extend past the boundaries of its container on the y axis.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/bleed
   */ bleedY: spaceProperty,
    /**
   * Allows an element to extend past the top boundary of its container.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/bleed
   */ bleedTop: spaceProperty,
    /**
   * Allows an element to extend past the bottom boundary of its container.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/bleed
   */ bleedBottom: spaceProperty,
    /**
   * Allows an element to extend past the left boundary of its container.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/bleed
   */ bleedLeft: spaceProperty,
    /**
   * Allows an element to extend past the right boundary of its container.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/bleed
   */ bleedRight: spaceProperty,
    /**
   * Sets the horizontal and vertical postion of a positioned element, applying offsets from the edges of the element’s containing block.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/inset
   */ inset: spaceProperty,
    /**
   * Sets the margin area on all four sides of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/padding
   */ margin: spaceProperty,
    /**
   * Sets the margin area on the bottom of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/margin
   */ marginBottom: spaceProperty,
    /**
   * Sets the margin area on the left of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/margin
   */ marginLeft: spaceProperty,
    /**
   * Sets the margin area on the right of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/margin
   */ marginRight: spaceProperty,
    /**
   * Sets the margin area on the top of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/margin
   */ marginTop: spaceProperty,
    /**
   * Sets the margin area on the left and right of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/margin
   */ marginX: spaceProperty,
    /**
   * Sets the margin area on the top and bottom of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/margin
   */ marginY: spaceProperty,
    /**
   * Sets the padding area on all four sides of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/padding
   */ padding: spaceProperty,
    /**
   * Sets the height of the padding area on the bottom of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/padding
   */ paddingBottom: spaceProperty,
    /**
   * Sets the width of the padding area on the left of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/padding
   */ paddingLeft: spaceProperty,
    /**
   * Sets the width of the padding area on the right of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/padding
   */ paddingRight: spaceProperty,
    /**
   * Sets the height of the padding area on the top of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/padding
   */ paddingTop: spaceProperty,
    /**
   * Sets the width of the padding area on the left and right of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/padding
   */ paddingX: spaceProperty,
    /**
   * Sets the height of the padding area on the top and bottom of an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/padding
   */ paddingY: spaceProperty
};
const transformProperty = property().accept(format.px);
const propertiesTransform = {
    /**
   * Translates the position of an element on the x axis.
   *
   * @deprecated Use `transform: translateX()` instead.
   */ x: property().accept(format.px),
    /**
   * Translates the position of an element on the y axis.
   *
   * @deprecated Use `transform: translateY()` instead.
   */ y: property().accept(format.px),
    /**
   * Resizes an element.
   *
   * @deprecated Use `transform: scale()` instead.
   */ scale: property().accept(format.number),
    /**
   * Rotates an element through the specified angle.
   *
   * @deprecated Use `transform: rotate()` instead.
   */ rotate: property().accept(format.deg),
    /**
   * Specifies a CSS transform to rotate, scale, skew or translate an element.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/transform
   */ transform: transformProperty
};
const propertiesLayout = {
    /**
   * Sets an item's alignment on the x axis, overriding the `alignX` value on the parent
   * [stack](https://sail.corp.stripe.com/next/apis/layout/stack) or [grid](https://sail.corp.stripe.com/next/foundations/layout#grids).
   *
   * **Initial value**: `stretch`
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/stack
   */ alignSelfX: property().accept(),
    /**
   * Sets an item's alignment on the y axis, overriding the `alignY` value on the parent
   * [stack](https://sail.corp.stripe.com/next/apis/layout/stack) or [grid](https://sail.corp.stripe.com/next/foundations/layout#grids).
   *
   * **Initial value**: `stretch`
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/stack
   */ alignSelfY: property().accept(),
    /**
   * Controls the alignment of items on the x axis. Items in [stacks](https://sail.corp.stripe.com/next/apis/layout/stack) stretch by default to fill the
   * available space, unless a specific alignment is set.
   *
   * **Initial value**: `stretch`
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/stack
   */ alignX: property().accept(),
    /**
   * Controls the alignment of items on the y axis. Items in [stacks](https://sail.corp.stripe.com/next/apis/layout/stack) stretch by default to fill the
   * available space, unless a specific alignment is set.
   *
   * **Initial value**: `stretch`
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/stack
   */ alignY: property().accept(),
    /**
   * An element can be displayed as a **block** that fills available space or
   * **inline** and adjacent to other inline elements. To control how items are
   * rendered _within_ an element, prefer to use the
   * [stack](https://sail.corp.stripe.com/next/apis/layout/stack)
   * or [grid](https://sail.corp.stripe.com/next/foundations/layout#grids)
   * layout patterns over `display: flex` and `display: grid`.
   *
   * @see https://sail.corp.stripe.com/next/foundations/layout#displays-and-layouts
   */ display: property().accept(),
    /**
   * Controls the spacing between rows and columns of items in a [stack](https://sail.corp.stripe.com/next/apis/layout/stack)
   * or [grid](https://sail.corp.stripe.com/next/foundations/layout#grids).
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/gap
   */ gap: property([
        tokens.space
    ]).accept(format.px),
    /**
   * Controls the spacing between columns of items in a [stack](https://sail.corp.stripe.com/next/apis/layout/stack)
   * or [grid](https://sail.corp.stripe.com/next/foundations/layout#grids).
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/column-gap
   */ gapX: property([
        tokens.space
    ]).accept(format.px),
    /**
   * Controls the spacing between rows of items in a [stack](https://sail.corp.stripe.com/next/apis/layout/stack)
   * or [grid](https://sail.corp.stripe.com/next/foundations/layout#grids).
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/row-gap
   */ gapY: property([
        tokens.space
    ]).accept(format.px),
    /**
   * Controls the spacing between columns of items in a [stack](https://sail.corp.stripe.com/next/apis/layout/stack)
   * or [grid](https://sail.corp.stripe.com/next/foundations/layout#grids).
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/column-gap
   */ columnGap: property([
        tokens.space
    ]).accept(format.px),
    /**
   * Controls the spacing between rows of items in a [stack](https://sail.corp.stripe.com/next/apis/layout/stack)
   * or [grid](https://sail.corp.stripe.com/next/foundations/layout#grids).
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/row-gap
   */ rowGap: property([
        tokens.space
    ]).accept(format.px),
    /**
   * A stack is an uniformly-spaced arrangement of items along either an x or y
   * axis. Stacks provide various options for spacing, aligning, and
   * distributing items. Stacks can also optionally wrap onto multiple lines to
   * create clusters of items.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/stack
   */ stack: property().accept(),
    /**
   * Controls whether items in a stack are packed together, or spread out. When packed, the
   * maximum space between items will be equal to the stack's gap. When spread out, items
   * will not stretch to fill the stack and the space between items will be maximized.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/stack
   */ distribute: property().accept(),
    /**
   * Controls whether a stack is allowed to wrap onto multiple lines.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/stack/#line-wrapping
   */ wrap: property().accept(),
    gridColumns: property().accept(format.number),
    /**
   * Arranges items into a tiled grid. Items will be a minimum of `minTileWidth` wide
   * and the number of columns will be determined by the available space.
   *
   * @see https://sail.corp.stripe.com/next/apis/layout/grid
   */ minTileWidth: property().accept(format.px)
};
const propertiesFont = {
    /**
   * The `baseline` property sets how an element aligns itself in relation
   * to its parent’s reference baseline. It accepts two keywords: a reference
   * baseline, and an optional alignment baseline.
   *
   * @deprecated Use `display` or `font` instead.
   *
   * @example
   * ```
   * <baseline>: 'normal' | 'cap-middle' | 'x-middle' | 'x-height';
   * ```
   *
   * @example
   * Defining a **reference baseline**:
   * ```
   * baseline: 'x-middle'
   * ```
   *
   * @example
   * Defining a **reference baseline** with a custom **alignment baseline**:
   * ```
   * baseline: 'x-middle normal'
   * ```
   *
   * @deprecated Use the `font` or `display` property instead.
   */ baseline: property().accept(),
    /**
   * The `baselineAlign` property controls whether an element’s children
   * automatically snap to the baseline.
   *
   * `baselineAlign: 'auto'` enables automatic baseline alignment and reserves
   * the `::before` pseudo-element to ensure that the reference font is
   * guaranteed to be the first available font in the baseline-sharing group’s
   * alignment context.
   *
   * `baselineAlign: 'manual'` enables automatic baseline alignment without
   * impacting the `::before` pseudo-element. Use with care; the first child
   * will establish the baseline-sharing group’s alignment context.
   *
   * `baselineAlign: 'none'` disables automatic baseline alignment.
   *
   * @see https://www.w3.org/TR/css-align-3/#align-by-baseline
   * @see https://sail.corp.stripe.com/next/apis/layout/vertical-alignment
   */ baselineAlign: property().accept(),
    /**
   * Sets the text size and line-height and any preset font-weight, font-family, and text-transform values.
   *
   * @see https://sail.stripe.me/tokens/typography/
   */ font,
    /**
   * Sets the `font-family` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/font-family
   */ fontFamily,
    /**
   * Sets the `font-size` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/font-size
   */ fontSize,
    /**
   * Controls the weight (or boldness) of the font, setting the `font-weight` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight
   */ fontWeight,
    /**
   * Controls the height of a line box, setting the `line-height` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/line-height
   */ lineHeight
};
const propertiesColor = {
    /**
   * Sets the text color and any corresponding icon, border, and background colors.
   *
   * @see https://sail.corp.stripe.com/next/tokens/color
   */ color,
    /**
   * Sets the `background-color` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/background-color
   */ backgroundColor,
    /**
   * Sets the `border-color` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-color
   */ borderColor,
    /**
   * Sets the `border-top-color` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-color
   */ borderTopColor,
    /**
   * Sets the `border-right-color` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-color
   */ borderRightColor,
    /**
   * Sets the `border-bottom-color` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-color
   */ borderBottomColor,
    /**
   * Sets the `border-left-color` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-color
   */ borderLeftColor,
    /**
   * Sets the fill color of an icon rendered via Sail's [Icon component](https://sail.corp.stripe.com/next/components/icon).
   *
   * @see https://sail.corp.stripe.com/next/tokens/color
   */ fill,
    /**
   * Sets the stroke color of an icon rendered via Sail's [Icon component](https://sail.corp.stripe.com/next/components/icon).
   *
   * @see https://sail.corp.stripe.com/next/tokens/color
   */ stroke,
    /**
   * Sets the text color without also setting any corresponding icon, border, and background colors.
   *
   * @see https://sail.corp.stripe.com/next/tokens/color
   */ textColor
};
const propertiesBoxShadow = {
    /**
   * Sets the `box-shadow` CSS property while retaining applied `focusRing`, `keyline` or `topShadow` styles.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/box-shadow
   */ boxShadow: property([
        tokens.shadow
    ]).accept(),
    /**
   * Applies focus indicator styles to an element, replacing the browser default [CSS outline](https://developer.mozilla.org/en-US/docs/Web/CSS/outline).
   */ focusRing: property([
        tokens.focus
    ]).accept(),
    /**
   * Applies a visual outline or border around an element. Unlike the [CSS border property](https://developer.mozilla.org/en-US/docs/Web/CSS/border), a keyline
   * will not affect the size of an element.
   */ keyline: property([
        tokens.color.border
    ]).accept(),
    /**
   * Sets the visual width of the applied keyline.
   */ keylineWidth: property([
        tokens.border.width
    ]).accept(format.px),
    /**
   * Sets an additional `box-shadow` on an element that can overlay other applied shadows.
   */ topShadow: property([
        tokens.shadow
    ]).accept(),
    /**
   * Rounds the corners of an element's outer border edge. Sets the `border-radius` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-radius
   */ borderRadius: property([
        tokens.border.radius
    ]).accept(format.px),
    /**
   * Rounds the corners of an element's top left outer border edge. Sets the `border-top-left-radius` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-left-radius
   */ borderTopLeftRadius: property([
        tokens.border.radius
    ]).accept(format.px),
    /**
   * Rounds the corners of an element's top right outer border edge. Sets the `border-top-right-radius` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-right-radius
   */ borderTopRightRadius: property([
        tokens.border.radius
    ]).accept(format.px),
    /**
   * Rounds the corners of an element's bottom right outer border edge. Sets the `border-bottom-right-radius` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-right-radius
   */ borderBottomRightRadius: property([
        tokens.border.radius
    ]).accept(format.px),
    /**
   * Rounds the corners of an element's bottom left outer border edge. Sets the `border-bottom-left-radius` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-left-radius
   */ borderBottomLeftRadius: property([
        tokens.border.radius
    ]).accept(format.px)
};
const propertiesNative = {
    /**
   * The **`white-space`** CSS property sets how white space inside an element is handled.
   *
   * **Initial value**: `normal`
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/white-space
   */ whiteSpace: property().accept(),
    /**
   * Sets the `background-clip` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/background-clip
   */ backgroundClip: property().accept(),
    /**
   * Sets the `background-image` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/background-image
   */ backgroundImage: property().accept(),
    /**
   * Sets the `background-position` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/background-position
   */ backgroundPosition: property().accept(),
    /**
   * Sets the `background-repeat` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/background-repeat
   */ backgroundRepeat: property().accept(),
    /**
   * Sets the `background-size` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/background-size
   */ backgroundSize: property().accept(),
    /**
   * Sets the `border` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border
   */ border: property().accept(),
    /**
   * Sets the `border-top` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-top
   */ borderTop: property().accept(),
    /**
   * Sets the `border-right` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-right
   */ borderRight: property().accept(),
    /**
   * Sets the `border-bottom` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom
   */ borderBottom: property().accept(),
    /**
   * Sets the `border-left` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-left
   */ borderLeft: property().accept(),
    /**
   * Sets the `border-style` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-style
   */ borderStyle: property([
        tokens.border.style
    ]).accept(),
    /**
   * Sets the `border-top-style` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-style
   */ borderTopStyle: property([
        tokens.border.style
    ]).accept(),
    /**
   * Sets the `border-right-style` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-style
   */ borderRightStyle: property([
        tokens.border.style
    ]).accept(),
    /**
   * Sets the `border-bottom-style` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-style
   */ borderBottomStyle: property([
        tokens.border.style
    ]).accept(),
    /**
   * Sets the `border-left-style` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-style
   */ borderLeftStyle: property([
        tokens.border.style
    ]).accept(),
    /**
   * Sets the `border-width` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-width
   */ borderWidth: property([
        tokens.border.width
    ]).accept(format.px),
    /**
   * Sets the `border-top-width` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-width
   */ borderTopWidth: property([
        tokens.border.width
    ]).accept(format.px),
    /**
   * Sets the `border-right-width` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-width
   */ borderRightWidth: property([
        tokens.border.width
    ]).accept(format.px),
    /**
   * Sets the `border-bottom-width` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-width
   */ borderBottomWidth: property([
        tokens.border.width
    ]).accept(format.px),
    /**
   * Sets the `border-left-width` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-width
   */ borderLeftWidth: property([
        tokens.border.width
    ]).accept(format.px),
    /**
   * Sets the `content` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/content
   */ content: property().accept(),
    /**
   * Sets the `position` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/position
   */ position: property().accept(),
    /**
   * Sets the `top` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/top
   */ top: property([
        tokens.space
    ]).accept(format.px),
    /**
   * Sets the `right` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/right
   */ right: property([
        tokens.space
    ]).accept(format.px),
    /**
   * Sets the `bottom` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/bottom
   */ bottom: property([
        tokens.space
    ]).accept(format.px),
    /**
   * Sets the `left` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/left
   */ left: property([
        tokens.space
    ]).accept(format.px),
    /**
   * Sets the `boxSizing` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/box-sizing
   */ boxSizing: property().accept(),
    /**
   * Sets the `animation` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/animation
   */ animation: property().accept(),
    /**
   * Sets the `animation-fill-mode` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/animation-fill-mode
   */ animationFillMode: property().accept(),
    /**
   * Sets the `animation-timeline` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/animation-timeline
   */ animationTimeline: property().accept(),
    /**
   * Sets the `appearance` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/appearance
   */ appearance: property().accept(),
    /**
   * Sets the `clip` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/clip
   */ clip: property().accept(),
    /**
   * Sets the `clip-path` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/clip-path
   */ clipPath: property().accept(),
    /**
   * Sets the `cursor` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/cursor
   */ cursor: property().accept(),
    /**
   * Sets the `filter` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/filter
   */ filter: property().accept(),
    /**
   * Sets the `font-variant-numeric` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/font-variant-numeric
   */ fontVariantNumeric: property().accept(),
    /**
   * Sets the `list-style` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/list-style
   */ listStyle: property().accept(),
    /**
   * Sets the `list-style-type` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/list-style-type
   */ listStyleType: property().accept(),
    /**
   * Sets the `object-fit` CSS property.
   *
   * see https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit
   */ objectFit: property().accept(),
    /**
   * Sets the `overflow` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/overflow
   */ overflow: property().accept(),
    /**
   * Sets the `overflow-y` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-y
   */ overflowY: property().accept(),
    /**
   * Sets the `overflow-x` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-x
   */ overflowX: property().accept(),
    /**
   * Sets the `opacity` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/opacity
   */ opacity: property().accept(),
    /**
   * Sets the `pointerEvents` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/pointer-events
   */ pointerEvents: property().accept(),
    /**
   * Sets the `resize` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/resize
   */ resize: property().accept(),
    /**
   * Sets the `scroll-margin-top` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/scroll-margin-top
   */ scrollMarginTop: spaceProperty,
    /**
   * Sets the `scrollbar-width` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/scrollbar-width
   */ scrollbarWidth: property().accept(),
    /**
   * Sets the `tab-size` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/tab-size
   */ tabSize: property().accept(),
    /**
   * Sets the `transition` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/transition
   */ transition: property().accept(),
    /**
   * Sets the `transition-property` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/transition-property
   */ transitionProperty: property().accept(),
    /**
   * Sets the `transition-duration` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/transition-duration
   */ transitionDuration: property([
        tokens.animation.duration
    ]).accept(format.ms),
    /**
   * Sets the `transition-timing-function` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/transition-timing-function
   */ transitionTimingFunction: property([
        tokens.animation.easing
    ]).accept(),
    /**
   * Sets the `transform-origin` CSS property.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/transform-origin
   */ transformOrigin: property().accept(),
    /**
   * Sets the `textAlign` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/text-align
   */ textAlign: property().accept(),
    /**
   * Sets the `textDecoration` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/text-decoration
   */ textDecoration: property().accept(),
    /**
   * Sets the `textDecorationStyle` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/text-decoration-style
   */ textDecorationStyle: property().accept(),
    /**
   * Sets the `textDecorationColor` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/text-decoration-color
   */ textDecorationColor: property().accept(),
    /**
   * Sets the `textOverflow` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/text-overflow
   */ textOverflow: property().accept(),
    /**
   * Sets the `textTransform` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/text-transform
   */ textTransform: property().accept(),
    /**
   * Sets the `textUnderlineOffset` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/text-underline-offset
   */ textUnderlineOffset: property().accept(),
    /**
   * Sets the `user-select` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/user-select
   */ userSelect: property().accept(),
    /**
   * Sets the `visibility` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/visibilty
   */ visibility: property().accept(),
    /**
   * Sets the `zIndex` CSS property.
   *
   * @see https://developer.mozilla.org/docs/Web/CSS/z-index
   */ zIndex: property().accept()
};
const builtInProperties = {
    ...propertiesSize,
    ...propertiesSpace,
    ...propertiesTransform,
    ...propertiesLayout,
    ...propertiesFont,
    ...propertiesColor,
    ...propertiesBoxShadow,
    ...propertiesNative
};
// This is a catch-all for any CSS properties that are not explicitly
// defined in this file. It allows us to use any CSS property without
// needing to process it first.
const vanillaProperties = {
    '*' (value) {
        return `${value}`;
    }
};
export const properties = {
    ...builtInProperties,
    ...vanillaProperties
};

//# sourceMappingURL=properties.js.map